import {
  Box,
  Banner,
  BlockStack,
  InlineStack,
  ProgressBar,
  Text,
  Badge,
  Button,
} from '@shopify/polaris';
import type { BulkJobDetailDto } from '../../types';

/**
 * T116 [US4]: BulkProgressBar — shows progress %, ETA, product count (FR-027).
 *
 * @see contracts/bulk.md, FR-027
 */

interface BulkProgressBarProps {
  job: BulkJobDetailDto;
  onCancel?: () => void;
}

export function BulkProgressBar({ job, onCancel }: BulkProgressBarProps) {
  const isActive = job.status === 'QUEUED' || job.status === 'RUNNING';
  const isComplete = job.status === 'COMPLETED';
  const isFailed = job.status === 'FAILED';
  const isCancelled = job.status === 'CANCELLED';

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="headingSm">
            {job.jobType} Job
          </Text>
          <StatusBadge status={job.status} />
        </InlineStack>
        {isActive && onCancel && (
          <Button variant="plain" tone="critical" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </InlineStack>

      <ProgressBar
        progress={job.progressPercent}
        size="small"
        tone={isFailed ? 'critical' : isComplete ? 'success' : 'highlight'}
      />

      <InlineStack align="space-between">
        <Text as="span" variant="bodySm" tone="subdued">
          {job.processedCount} / {job.totalProducts} products
          {job.successCount > 0 && ` (${job.successCount} succeeded)`}
          {job.failureCount > 0 && ` (${job.failureCount} failed)`}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {isActive && job.estimatedMinutesRemaining != null
            ? `~${job.estimatedMinutesRemaining} min remaining`
            : isComplete
              ? 'Completed'
              : isCancelled
                ? 'Cancelled'
                : isFailed
                  ? 'Failed'
                  : 'Queued'}
        </Text>
      </InlineStack>

      {isFailed && job.errorDetails.length > 0 && (
        <Banner tone="critical" title="Some products failed">
          <Box>
            <Text as="p" variant="bodySm">
              {job.errorDetails.length} product(s) encountered errors during processing.
            </Text>
          </Box>
        </Banner>
      )}
    </BlockStack>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'COMPLETED':
      return <Badge tone="success">Completed</Badge>;
    case 'RUNNING':
      return <Badge tone="attention">Running</Badge>;
    case 'QUEUED':
      return <Badge tone="info">Queued</Badge>;
    case 'FAILED':
      return <Badge tone="critical">Failed</Badge>;
    case 'CANCELLED':
      return <Badge>Cancelled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}
